import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdminRole } from '../../lib/roles';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  ShieldAlert, UserPlus, UserCog, KeyRound, Ban, Crown, Mail, Phone, CheckCircle2,
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'operation_admin', label: 'Operation Admin', description: 'Day-to-day CMS access (bookings, KYC, leads)', color: 'bg-red-100 text-red-800' },
  { value: 'pic', label: 'PIC', description: 'Approves claims (stage 1)', color: 'bg-blue-100 text-blue-800' },
  { value: 'account', label: 'Account', description: 'Finance — verifies payments & pays claims', color: 'bg-purple-100 text-purple-800' },
];

const SUPER_ADMIN_EMAIL = 'admin@samrumah.com';

export default function AdminTeamMembers() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', full_name: '', phone: '', role: 'pic', password: '' });

  // Role change dialog
  const [roleTarget, setRoleTarget] = useState(null);
  const [newRole, setNewRole] = useState('');

  // Password reset dialog
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const isSuperAdmin = isSuperAdminRole(user?.role) || (user?.role === 'admin' && user?.email === SUPER_ADMIN_EMAIL);

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/team-members');
      setMembers(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) loadMembers();
  }, [isSuperAdmin, loadMembers]);

  const handleCreate = async () => {
    if (!newMember.email || !newMember.full_name || !newMember.password) {
      toast.error('Email, name, and password are required');
      return;
    }
    if (newMember.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/team-members', newMember);
      toast.success(`${newMember.full_name} added as ${newMember.role.toUpperCase()}`);
      setCreateOpen(false);
      setNewMember({ email: '', full_name: '', phone: '', role: 'pic', password: '' });
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create team member');
    }
  };

  const handleRoleChange = async () => {
    if (!roleTarget || !newRole) return;
    try {
      await api.put(`/team-members/${roleTarget.id}/role`, { role: newRole });
      toast.success(`Role changed to ${newRole.toUpperCase()}`);
      setRoleTarget(null);
      setNewRole('');
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change role');
    }
  };

  const handlePasswordReset = async () => {
    if (!pwdTarget || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/team-members/${pwdTarget.id}/reset-password`, { password: newPassword });
      toast.success(`Password reset for ${pwdTarget.full_name}`);
      setPwdTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeactivate = async (member) => {
    if (!window.confirm(`Deactivate ${member.full_name}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/team-members/${member.id}`);
      toast.success(`${member.full_name} deactivated`);
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleReactivate = async (member) => {
    try {
      await api.put(`/team-members/${member.id}`, { status: 'active' });
      toast.success(`${member.full_name} reactivated`);
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const roleMeta = (role) => ROLE_OPTIONS.find(r => r.value === role) || { label: (role || 'UNKNOWN').toString().toUpperCase(), color: 'bg-gray-100 text-gray-800' };

  if (!isSuperAdmin) {
    return (
      <Card className="border-amber-200" data-testid="team-members-forbidden">
        <CardContent className="pt-6 text-center">
          <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="text-base font-semibold">Super Admin access required</p>
          <p className="text-sm text-gray-500 mt-1">Only the super admin can manage team members.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-members-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-500 mt-1">Manage Admin, PIC, and Account users. Agents and customers are managed separately.</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#4E50FF] hover:bg-[#3436CC] text-white"
          data-testid="add-team-member-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Role summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ROLE_OPTIONS.map((r) => {
          const count = members.filter(m => m.role === r.value && m.status !== 'inactive').length;
          return (
            <Card key={r.value} className="border-gray-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center justify-between">
                  {r.label}
                  <Badge className={r.color}>{count}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500">{r.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Members table */}
      <Card>
        <CardHeader>
          <CardTitle>All Team Members ({members.length})</CardTitle>
          <CardDescription>Super admin has full control. Changing role of super admin is not allowed.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No team members yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>User ID</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => {
                  const meta = roleMeta(m.role);
                  const isSuper = m.is_super_admin;
                  return (
                    <TableRow key={m.id} data-testid={`team-member-${m.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="font-medium">{m.full_name}</div>
                          {isSuper && (
                            <Badge className="bg-amber-100 text-amber-800 border-0">
                              <Crown className="w-3 h-3 mr-1" />Super Admin
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {m.user_code ? (
                          <span className="font-mono text-xs text-[#4E50FF] bg-[#4E50FF]/5 px-2 py-1 rounded">
                            {m.user_code}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="w-3 h-3 text-gray-400" />{m.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {m.phone ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3 text-gray-400" />{m.phone}
                          </div>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={meta.color}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {m.status === 'inactive' ? (
                          <Badge variant="secondary" className="bg-gray-100 text-gray-600">Inactive</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" />Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {!isSuper ? (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setRoleTarget(m); setNewRole(m.role); }}
                              data-testid={`change-role-${m.id}`}
                            >
                              <UserCog className="w-3 h-3 mr-1" />Role
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPwdTarget(m)}
                              data-testid={`reset-pwd-${m.id}`}
                            >
                              <KeyRound className="w-3 h-3 mr-1" />Reset PW
                            </Button>
                            {m.status === 'inactive' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-emerald-300 text-emerald-700"
                                onClick={() => handleReactivate(m)}
                                data-testid={`reactivate-${m.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />Reactivate
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700"
                                onClick={() => handleDeactivate(m)}
                                data-testid={`deactivate-${m.id}`}
                              >
                                <Ban className="w-3 h-3 mr-1" />Deactivate
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Protected</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-team-member-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Create a new Operation Admin, PIC, or Account user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Full Name *</Label>
              <Input value={newMember.full_name} onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })} placeholder="e.g., Sarah Lim" data-testid="new-member-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} placeholder="e.g., sarah.pic@samrumah.com" data-testid="new-member-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={newMember.phone} onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })} placeholder="+60123456789" data-testid="new-member-phone" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                <SelectTrigger data-testid="new-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label} — <span className="text-xs text-gray-500">{r.description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temporary Password *</Label>
              <Input type="text" value={newMember.password} onChange={(e) => setNewMember({ ...newMember, password: e.target.value })} placeholder="Min 6 characters" data-testid="new-member-password" />
              <p className="text-xs text-gray-500 mt-1">User can change password after first login.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="create-member-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!roleTarget} onOpenChange={(o) => !o && setRoleTarget(null)}>
        <DialogContent data-testid="change-role-dialog">
          <DialogHeader>
            <DialogTitle>Change Role — {roleTarget?.full_name}</DialogTitle>
            <DialogDescription>Current role: <Badge className={roleMeta(roleTarget?.role).color}>{roleMeta(roleTarget?.role).label}</Badge></DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>New Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="new-role-select"><SelectValue placeholder="Select a new role" /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} disabled={r.value === roleTarget?.role}>
                    {r.label} — <span className="text-xs text-gray-500">{r.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-amber-600 mt-3">
              ⚠️ Only roles within Operation Admin, PIC, or Account are allowed here.
              Agents and Customers are managed separately in their own sections.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleTarget(null)}>Cancel</Button>
            <Button onClick={handleRoleChange} disabled={!newRole || newRole === roleTarget?.role} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="change-role-submit">Confirm Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password — {pwdTarget?.full_name}</DialogTitle>
            <DialogDescription>Enter a new temporary password. The user should change it after login.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>New Password</Label>
            <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdTarget(null)}>Cancel</Button>
            <Button onClick={handlePasswordReset} disabled={newPassword.length < 6} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="reset-password-submit">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
